
import React from 'react';
import { ChartConfig } from './types';


const MockLineChart: React.FC<{ data: any[] }> = ({ data }) => {

    return (
        <div className="relative h-[240px] w-full p-4">
            {/* Y-Axis Labels */}
            <div className="absolute top-0 left-0 h-full w-8 text-right text-xs text-gray-400 space-y-[41px] pt-0.5">
                <div>1L</div>
                <div>90k</div>
                <div>80k</div>
                <div>70k</div>
                <div>60k</div>
                <div>50k</div>
            </div>

            {/* X-Axis Labels */}
            <div className="absolute bottom-0 left-8 right-0 flex justify-between text-xs text-gray-500">
                <span className='w-[19%] text-center'>Week 1</span>
                <span className='w-[19%] text-center'>Week 2</span>
                <span className='w-[19%] text-center'>Week 3</span>
                <span className='w-[19%] text-center'>Week 4</span>
                <span className='w-[19%] text-center'>Week 5</span>
            </div>


            <div className="absolute top-0 left-8 right-0 h-[88%]">

                {[...Array(6)].map((_, i) => (
                    <div key={i} className="border-t border-gray-200 absolute w-full"
                        style={{ top: `${i * 20}%` }}
                    />
                ))}
                {[...Array(6)].map((_, i) => (
                    <div key={i} className={`border-l border-gray-100 absolute h-full ${i === 0 || i === 5 ? 'border-gray-200' : ''}`}
                        style={{ left: `${i * 20}%` }}
                    />
                ))}
            </div>


            <div className="absolute top-0 left-8 right-0 h-[88%] overflow-hidden">

                <div
                    className="w-full h-full absolute"
                    style={{

                        clipPath: 'polygon(0% 30%, 25% 45%, 50% 60%, 75% 50%, 100% 45%, 100% 90%, 0% 90%)',
                        background: 'linear-gradient(to top, rgba(255, 77, 79, 0.1), rgba(255, 255, 255, 0.5))',
                        opacity: 0.8,
                    }}
                />


                <div
                    className="absolute w-full h-[3px] bg-red-500"
                    style={{

                        clipPath: 'polygon(0% 30%, 25% 45%, 50% 60%, 75% 50%, 100% 45%)',
                        top: 0,
                        zIndex: 10,

                    }}
                />

                {/* Constant 50k baseline (The lowest grid line in the design) */}
                <div className="absolute bottom-[12%] w-full h-[1px] bg-gray-300" />
            </div>
        </div>
    );
};


// 2. Lead Conversion Breakdown (Mock Donut Chart - remains the same, it was already a good approximation)
const MockPieChart: React.FC<{ data: any[] }> = ({ data }) => {
    const total = data.reduce((sum, item) => sum + item.value, 0);
    return (
        <div className="flex justify-around items-center h-[240px] px-4">
            {/* Mock Donut */}
            <div className="relative h-40 w-40 rounded-full bg-white shadow-inner">
                <div className="absolute inset-0 rounded-full" style={{ background: `conic-gradient(#40a9ff 0% 40%, #faad14 40% 65%, #9254de 65% 85%, #52c41a 85% 100%)` }}>
                    <div className="absolute inset-4 bg-white rounded-full" />
                </div>
            </div>

            {/* Mock Legend */}
            <div className="flex flex-col space-y-2 text-sm ml-4">
                {data.map((item, index) => (
                    <div key={index} className="flex items-center">
                        <div
                            className="w-2 h-2 rounded-full mr-2"
                            style={{ backgroundColor: item.color }}
                        />
                        <span className="text-gray-700">
                            {item.type} (**{Math.round((item.value / total) * 100)}%**)
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
};

// 3. Daily Activity Trends (Mock Multi-Line Chart)
const MockMultiLineChart: React.FC<{ data: any[] }> = ({ data }) => {
    const legendMap = {
        'Leads Added': '#9254de', // Purple
        'Calls Made': '#52c41a', // Green
        'Follow-ups Done': '#ff4d4f', // Red
        'Meetings Done': '#40a9ff', // Blue
    };

    // Adjusted Y-axis labels and spacing to match the design (0, 5, 10, 15, 20)
    return (
        <div className="relative h-[260px] w-full p-4">
            {/* Y-Axis Labels and Grid */}
            <div className="absolute top-0 left-0 h-full w-8 text-right text-xs text-gray-400 space-y-[45px] pt-1">
                <div>20</div>
                <div>15</div>
                <div>10</div>
                <div>5</div>
                <div>0</div>
            </div>

            {/* Grid Lines (h-[85%] used to align the 0 line with the X-axis text) */}
            <div className="absolute top-0 left-8 right-0 h-[85%] border-l border-gray-200">
                {/* Horizontal Grid Lines */}
                {[...Array(5)].map((_, i) => (
                    <div key={i} className="border-t border-gray-200 absolute w-full"
                        style={{ top: `${i * 25}%` }}
                    />
                ))}
            </div>

            {/* X-Axis Labels (Aligned horizontally with the bottom of the chart area) */}
            <div className="absolute bottom-0 left-8 right-0 flex justify-between text-xs text-gray-500">
                <span className='w-[13%] text-center'>Mon</span>
                <span className='w-[13%] text-center'>Tue</span>
                <span className='w-[13%] text-center'>Wed</span>
                <span className='w-[13%] text-center'>Thu</span>
                <span className='w-[13%] text-center'>Fri</span>
                <span className='w-[13%] text-center'>Sat</span>
                <span className='w-[13%] text-center'>Mon</span>
            </div>

            {/* Mock Lines (H-[85%] to match grid area) */}
            <div className="absolute top-0 left-8 right-0 h-[100%]">
                {/* NOTE: We are using top position to set the baseline and clip-path to create the curve around that baseline.
                         The top % represents the average vertical position on the 0%-100% chart height scale (20 to 0). */}

                {/* Line 1 (Calls Made - Green, Highest Line, average near 15) */}
                <div className="absolute w-full h-[3px] bg-green-500"
                    style={{
                        // Top 25% = approx 15 on the 20 scale.
                        top: '25%',
                        // Curve: Starts high, dips slightly, peaks, slightly dips again
                        clipPath: 'polygon(0% 50%, 14% 35%, 28% 40%, 42% 30%, 56% 40%, 70% 35%, 84% 45%, 100% 40%)',
                        zIndex: 4
                    }}
                />

                {/* Line 2 (Follow-ups Done - Red, High Medium Line, average near 12) */}
                <div className="absolute w-full h-[3px] bg-red-500"
                    style={{
                        // Top 45% = approx 11 on the 20 scale.
                        top: '45%',
                        // Curve: Starts lower, peaks, dips to lowest point (Fri), then rises
                        clipPath: 'polygon(0% 50%, 14% 60%, 28% 50%, 42% 60%, 56% 50%, 70% 40%, 84% 50%, 100% 60%)',
                        zIndex: 3
                    }}
                />

                {/* Line 3 (Leads Added - Purple, Low Medium Line, average near 9) */}
                <div className="absolute w-full h-[3px] bg-purple-500"
                    style={{
                        // Top 55% = approx 9 on the 20 scale.
                        top: '55%',
                        // Curve: Starts low, peaks high (Wed/Thu), dips low (Fri), rises to average
                        clipPath: 'polygon(0% 60%, 14% 50%, 28% 40%, 42% 50%, 56% 45%, 70% 60%, 84% 50%, 100% 55%)',
                        zIndex: 2
                    }}
                />

                {/* Line 4 (Meetings Done - Blue, Lowest Line, average near 6) */}
                <div className="absolute w-full h-[3px] bg-blue-500"
                    style={{
                        // Top 70% = approx 6 on the 20 scale.
                        top: '70%',
                        // Curve: Starts average, dips low (Fri/Sat), rises slightly
                        clipPath: 'polygon(0% 50%, 14% 55%, 28% 60%, 42% 50%, 56% 65%, 70% 70%, 84% 65%, 100% 70%)',
                        zIndex: 1
                    }}
                />
            </div>

            {/* Legend (Bottom of the graph area) */}
            <div className="absolute bottom-0 left-8 right-0 flex flex-wrap justify-center text-xs space-x-3 mt-6">
                {Object.entries(legendMap).map(([metric, color]) => (
                    <div key={metric} className="flex items-center">
                        <div className="w-2 h-2 rounded-full mr-1" style={{ backgroundColor: color }} />
                        <span className="text-gray-700">{metric}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

// --- Main Renderer ---

const ChartRenderer: React.FC<{ config: ChartConfig }> = ({ config }) => {
    const { chartType, data } = config;

    switch (chartType) {
        case 'Line':
            return <MockLineChart data={data} />;
        case 'Pie':
            return <MockPieChart data={data} />;
        case 'MultiLine':
            return <MockMultiLineChart data={data} />;
        default:
            return (
                <div className="p-4 text-center text-gray-500 h-[250px] flex items-center justify-center">
                    Unknown Chart Type: {chartType}
                </div>
            );
    }
};

export default ChartRenderer;